// Frontend mirror of backend granular RBAC. Backend server.py is the enforcer.
export const MODULES = ["dashboard", "customers", "suppliers", "products", "inventory", "sales",
  "quotations", "purchase", "purchase_bills", "production", "dispatch", "samples", "qc_tests", "qc_parameters", "payments",
  "expenses", "bank", "gst", "documents", "reports", "company_settings", "users",
  "hr_employees", "hr_attendance", "hr_leaves", "hr_payroll", "hr_daily", "hr_self",
  "sea_freight", "sea_freight_admin"];
export const ACTIONS = ["view", "create", "edit", "delete", "export_pdf", "export_excel", "approve", "receive"];

const V = ["view"];
const VCE = ["view", "create", "edit", "export_pdf", "export_excel"];

export const ROLE_PRESETS = {
  admin: null,
  sales: { dashboard: V, customers: ["view", "create", "edit"], quotations: [...VCE, "approve"], sales: [...VCE, "approve"], samples: ["view", "create"], dispatch: V, payments: V, reports: ["view", "export_pdf", "export_excel"] },
  accounts: { dashboard: V, sales: VCE, purchase: ["view", "edit", "export_pdf", "export_excel"], purchase_bills: [...VCE, "approve"], payments: [...VCE, "approve", "delete"], expenses: [...VCE, "delete"], bank: [...VCE, "delete", "approve"], gst: [...VCE, "delete"], customers: V, suppliers: V, reports: ["view", "export_pdf", "export_excel"] },
  purchase: { dashboard: V, suppliers: ["view", "create", "edit"], purchase: [...VCE, "approve"], purchase_bills: ["view", "export_pdf"], products: V, inventory: V, reports: ["view", "export_pdf", "export_excel"] },
  production: { dashboard: V, production: [...VCE, "receive"], products: V, inventory: V, reports: ["view", "export_pdf"] },
  store: { dashboard: V, products: ["view", "create", "edit"], inventory: ["view", "create", "edit"], production: ["view", "receive"], purchase_bills: ["view", "export_pdf"], dispatch: V, reports: V },
  dispatch: { dashboard: V, dispatch: VCE, sales: V, customers: V, reports: ["view", "export_pdf"] },
  qc_sample: { dashboard: V, samples: VCE, customers: V, products: V },
  qc_lab: { dashboard: V, qc_tests: [...VCE, "approve"], qc_parameters: VCE, samples: ["view", "create", "export_pdf"], products: V, suppliers: V, reports: V },
  hr: { dashboard: V, hr_employees: VCE, hr_attendance: [...VCE, "delete", "approve"], hr_leaves: [...VCE, "delete", "approve"], hr_payroll: [...VCE, "delete", "approve"], hr_daily: [...VCE, "delete", "approve"], hr_self: ["view", "create"] },
  employee: { hr_self: ["view", "create"] },
};
for (const preset of Object.values(ROLE_PRESETS)) {
  if (preset && preset.dashboard && !preset.documents) preset.documents = V;
}

const PATH_MODULE = {
  "/": "dashboard", "/customers": "customers", "/suppliers": "suppliers", "/products": "products",
  "/inventory": "inventory", "/sales": "sales", "/purchase": "purchase_bills", "/purchase-orders": "purchase", "/production": "production",
  "/dispatch": "dispatch", "/samples": "samples", "/payments": "payments", "/dues": "payments",
  "/qc-tests": "qc_tests", "/qc-parameters": "qc_parameters",
  "/opening-stock": "inventory",
  "/expenses": "expenses",
  "/assets": "expenses",
  "/job-work": "production", "/job-work-vendors": "production", "/assets": "expenses",
  "/bank": "bank",
  "/gst": "gst",
  "/sea-freight": "sea_freight",
  "/sea-freight/rates": "sea_freight",
  "/sea-freight/news": "sea_freight",
  "/sea-freight/ports": "sea_freight",
  "/sea-freight/vessels": "sea_freight",
  "/sea-freight/routes": "sea_freight",
  "/sea-freight/analytics": "sea_freight",
  "/sea-freight/saved-routes": "sea_freight",
  "/sea-freight/quotations": "sea_freight",
  "/sea-freight/alerts": "sea_freight",
  "/sea-freight/data-sources": "sea_freight",
  "/sea-freight/settings": "sea_freight_admin",
  "/documents": "documents",
  "/my-hr": "hr_self",
  "/attendance/daily": "hr_attendance",
  "/attendance/register": "hr_attendance",
  "/attendance-corrections": "hr_attendance",
  "/payroll-run": "hr_payroll",
  "/ledger": "payments",
  "/hr": "hr_employees",
  "/employees": "hr_employees", "/attendance": "hr_attendance", "/leaves": "hr_leaves", "/payroll": "hr_payroll",
  "/daily-workers": "hr_daily", "/daily-attendance": "hr_daily", "/daily-attendance/bulk": "hr_daily", "/daily-wages": "hr_daily",
  "/worker-attendance/register": "hr_daily",
  "/reports": "reports", "/profit-loss": "bank", "/company": "company_settings", "/users": "users", "/audit": "users",
};
const RES_MODULE = { "job-work-vendors": "production", customers: "customers", suppliers: "suppliers", dispatches: "dispatch", payments: "payments", samples: "samples", products: "products", inventory: "inventory", "daily-workers": "hr_daily", "daily-attendance": "hr_daily", "daily-wages": "hr_daily" };

export const effectivePerms = (user) => {
  if (!user) return {};
  if (user.role === "admin") return null; // full
  if (user.permissions && Object.keys(user.permissions).length) return user.permissions;
  return ROLE_PRESETS[user.role] || { dashboard: ["view"] };
};

export const hasPerm = (user, module, action) => {
  if (!user) return false;
  const p = effectivePerms(user);
  if (p === null) return true; // admin
  return !!(p[module] && p[module].includes(action));
};

export const canAccess = (user, path) => {
  if (path === "/change-password") return true;
  if (user?.role === "qc_lab" && (path === "/products" || path === "/suppliers")) return false;
  if (path === "/requests") return !!user;
  if (path === "/notifications") return !!user;
  if (path === "/request-permissions") return user?.role === "admin";
  if (path === "/sales") return hasPerm(user, "sales", "view") || hasPerm(user, "quotations", "view");
  const m = PATH_MODULE[path];
  if (!m) return user?.role === "admin";
  return hasPerm(user, m, "view");
};

export const canWrite = (user, resource) => hasPerm(user, RES_MODULE[resource] || resource, "edit") || hasPerm(user, RES_MODULE[resource] || resource, "create");
export const canDelete = (user, resource) => hasPerm(user, RES_MODULE[resource] || resource, "delete");

export const ROLE_LABELS = {
  admin: "Director / Admin", sales: "Sales", purchase: "Purchase", production: "Production",
  dispatch: "Dispatch", accounts: "Accounts", qc_sample: "QC / Sample", store: "Store", qc_lab: "QC / LAB",
  hr: "HR / Admin", employee: "Employee (self service)",
};
export const ROLE_OPTIONS = ["admin", "hr", "employee", "sales", "purchase", "production", "dispatch", "accounts", "qc_sample", "qc_lab", "store"];
export const MODULE_LABELS = {
  dashboard: "Dashboard", customers: "Customers", suppliers: "Suppliers", products: "Products",
  inventory: "Inventory", sales: "Sales", quotations: "Quotations", purchase: "Purchase Orders",
  purchase_bills: "Purchase Bills",
  production: "Production", dispatch: "Dispatch", samples: "Samples / QC", payments: "Payments / Dues",
  qc_tests: "Material QC / Testing", qc_parameters: "QC Parameters",
  expenses: "Expenses",
  bank: "Company Bank Balance",
  gst: "GST / Tax",
  documents: "Documents Library",
  hr_employees: "Employees", hr_attendance: "Attendance", hr_leaves: "Leave Requests", hr_payroll: "Payroll", hr_daily: "Daily Workers",
  hr_self: "My HR (self service)",
  reports: "Reports", company_settings: "Company Settings", users: "User Management",
  sea_freight: "Sea Freight Intelligence", sea_freight_admin: "Sea Freight — Data Sources & API Keys",
};
export const ACTION_LABELS = { view: "View", create: "Create", edit: "Edit", delete: "Delete", export_pdf: "PDF", export_excel: "Excel", approve: "Approve", receive: "Receive" };
